import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SettingsIcon from '@mui/icons-material/Settings';
import ShuffleIcon from '@mui/icons-material/Shuffle';
import UIContainer from 'components/ui/UIContainer';
import BreadCrumbs from 'components/layouts/BreadCrumbs';
import UIEmptyState from 'components/ui/UIEmptyState';
import WithLoader from 'components/WithLoader';
import { axiosInstance } from 'setup/axiosInstance';
import {
  LearningResultStatus,
  LearningSession,
  LearningSessionWord,
  MissingWordsExercise,
  MissingWordsExerciseResponse,
  QuestionAnswerExercise,
  QuestionAnswerExerciseResponse,
} from 'app/types';
import { fillUrlIds, pagePaths } from 'routes/paths';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';

type Step =
  | 'SETUP'
  | 'MEMORY'
  | 'MATCHING'
  | 'MISSING_WORDS'
  | 'QUESTION_ANSWER'
  | 'QUIZ'
  | 'DONE';
type MemoryColumnKey = 'word' | 'definition' | 'translation';
type MemoryColumnVisibility = Record<MemoryColumnKey, boolean>;
type MemoryColumnBlur = Record<MemoryColumnKey, boolean>;
type MatchingSide = 'left' | 'right';
type ExerciseOptionKey =
  | 'memory'
  | 'matching'
  | 'missingWords'
  | 'questionAnswer';
type UrlStep = Exclude<Step, 'SETUP' | 'DONE'>;

const DEFAULT_MEMORY_COLUMN_ORDER: MemoryColumnKey[] = [
  'word',
  'definition',
  'translation',
];
const DEFAULT_MEMORY_COLUMN_VISIBILITY: MemoryColumnVisibility = {
  word: true,
  definition: false,
  translation: true,
};
const DEFAULT_MEMORY_COLUMN_BLUR: MemoryColumnBlur = {
  word: false,
  definition: false,
  translation: true,
};
const MEMORY_COLUMN_LABELS: Record<MemoryColumnKey, string> = {
  word: 'Слово',
  definition: 'Пояснення',
  translation: 'Переклад',
};
const LEARNING_EXERCISE_TEXT_SX = { xs: 20, md: 24 };

const resultLabels: Record<LearningResultStatus, string> = {
  LEARNED: 'Знаю',
  NOT_LEARNED: 'Не знаю',
  REPEAT: 'Потребує повторення',
};

const formatLearningSessionTitle = (createdAt?: string) =>
  createdAt ? dayjs(createdAt).format('DD.MM.YYYY HH:mm') : undefined;

const exerciseBreadcrumbLabels: Partial<Record<Step, string>> = {
  MEMORY: 'Таблиця для запам’ятовування',
  MATCHING: 'Знайди відповідність',
  MISSING_WORDS: 'Пропущені слова',
  QUESTION_ANSWER: 'Запитання та відповідь',
  QUIZ: 'Збереження результатів',
};
const exerciseStepUrlSegments: Record<UrlStep, string> = {
  MEMORY: 'memory',
  MATCHING: 'matching',
  MISSING_WORDS: 'missing-words',
  QUESTION_ANSWER: 'question-answer',
  QUIZ: 'quiz',
};
const urlSegmentExerciseSteps: Record<string, Step> = Object.entries(
  exerciseStepUrlSegments,
).reduce(
  (acc, [step, segment]) => ({
    ...acc,
    [segment]: step as Step,
  }),
  {},
);
const exerciseOptions: {
  checked: ExerciseOptionKey;
  description: string;
  title: string;
}[] = [
  {
    checked: 'memory',
    description:
      'Приховуй колонки, змінюй порядок і перемішуй слова, щоб перевірити себе.',
    title: 'Таблиця для запам’ятовування',
  },
  {
    checked: 'matching',
    description:
      'Обирай слово і його переклад у двох колонках, щоб скласти правильні пари.',
    title: 'Знайди відповідність',
  },
  {
    checked: 'missingWords',
    description:
      'Віднови речення, вставивши слово відповідно до контексту.',
    title: 'Пропущені слова',
  },
  {
    checked: 'questionAnswer',
    description:
      'Отримай реальне розмовне запитання і дай відповідь, використавши потрібне слово.',
    title: 'Запитання та відповідь',
  },
];

const shuffle = <T,>(items: T[]) =>
  [...items]
    .map((item) => ({ item, order: Math.random() }))
    .sort((a, b) => a.order - b.order)
    .map(({ item }) => item);

const getWordTitle = (word: LearningSessionWord) =>
  word.wordKind === 'IRREGULAR_VERB'
    ? word.infinitive || word.word || ''
    : word.word || '';

const getWordHint = (word: LearningSessionWord) =>
  word.definition || word.description || 'Опис відсутній';

const getWordForms = (word: LearningSessionWord) => {
  if (word.wordKind !== 'IRREGULAR_VERB') {
    return word.transcription || '';
  }

  return [word.pastSimple, word.pastParticiple].filter(Boolean).join(' / ');
};

const LearningSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const { learningExerciseStep = '', learningSessionId = '' } = useParams();
  const [session, setSession] = useState<LearningSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState<Step>('SETUP');
  const [useMemory, setUseMemory] = useState(false);
  const [useMatching, setUseMatching] = useState(false);
  const [useMissingWords, setUseMissingWords] = useState(false);
  const [useQuestionAnswer, setUseQuestionAnswer] = useState(false);
  const [memorySettingsOpen, setMemorySettingsOpen] = useState(false);
  const [memoryColumnVisibility, setMemoryColumnVisibility] =
    useState<MemoryColumnVisibility>(DEFAULT_MEMORY_COLUMN_VISIBILITY);
  const [blurredMemoryColumns, setBlurredMemoryColumns] =
    useState<MemoryColumnBlur>(DEFAULT_MEMORY_COLUMN_BLUR);
  const [memoryColumnOrder, setMemoryColumnOrder] = useState<MemoryColumnKey[]>(
    DEFAULT_MEMORY_COLUMN_ORDER,
  );
  const [draftMemoryColumnVisibility, setDraftMemoryColumnVisibility] =
    useState<MemoryColumnVisibility>(DEFAULT_MEMORY_COLUMN_VISIBILITY);
  const [draftBlurredMemoryColumns, setDraftBlurredMemoryColumns] =
    useState<MemoryColumnBlur>(DEFAULT_MEMORY_COLUMN_BLUR);
  const [draftMemoryColumnOrder, setDraftMemoryColumnOrder] = useState<
    MemoryColumnKey[]
  >(DEFAULT_MEMORY_COLUMN_ORDER);
  const [memoryWords, setMemoryWords] = useState<LearningSessionWord[]>([]);
  const [leftWords, setLeftWords] = useState<LearningSessionWord[]>([]);
  const [rightWords, setRightWords] = useState<LearningSessionWord[]>([]);
  const leftWordRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const rightWordRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const previousLeftWordPositions = useRef<Map<number, DOMRect>>(new Map());
  const previousRightWordPositions = useRef<Map<number, DOMRect>>(new Map());
  const [selectedLeftId, setSelectedLeftId] = useState<number | null>(null);
  const [selectedRightId, setSelectedRightId] = useState<number | null>(null);
  const [matchedIds, setMatchedIds] = useState<number[]>([]);
  const [wrongLeftId, setWrongLeftId] = useState<number | null>(null);
  const [wrongRightId, setWrongRightId] = useState<number | null>(null);
  const [missingWordsExercises, setMissingWordsExercises] = useState<
    MissingWordsExercise[]
  >([]);
  const [missingWordsLoading, setMissingWordsLoading] = useState(false);
  const [missingWordsIndex, setMissingWordsIndex] = useState(0);
  const [missingWordsAnswers, setMissingWordsAnswers] = useState<
    Record<number, string>
  >({});
  const [missingWordsManualAnswers, setMissingWordsManualAnswers] = useState<
    Record<number, string>
  >({});
  const [checkedMissingWords, setCheckedMissingWords] = useState<
    Record<number, boolean>
  >({});
  const [questionAnswerExercises, setQuestionAnswerExercises] = useState<
    QuestionAnswerExercise[]
  >([]);
  const [questionAnswerLoading, setQuestionAnswerLoading] = useState(false);
  const [questionAnswerIndex, setQuestionAnswerIndex] = useState(0);
  const [questionAnswerResponses, setQuestionAnswerResponses] = useState<
    Record<number, string>
  >({});
  const [
    shownQuestionAnswerSamples,
    setShownQuestionAnswerSamples,
  ] = useState<Record<number, boolean>>({});
  const [quizIndex, setQuizIndex] = useState(0);
  const [results, setResults] = useState<Record<number, LearningResultStatus>>(
    {},
  );
  const [error, setError] = useState('');

  const words = useMemo(() => session?.words || [], [session]);
  const matchedWordIds = matchedIds;
  const matchedDone = matchedWordIds.length >= words.length;
  const quizDone = words.every((word) => results[word.id]);
  const exerciseBreadcrumbLabel = exerciseBreadcrumbLabels[step];
  const hasSelectedExercises =
    useMemory || useMatching || useMissingWords || useQuestionAnswer;

  const getSessionUrl = () =>
    fillUrlIds(pagePaths.learningSession, {
      learningSessionId,
    });

  const getStepUrl = (nextStep: Step) => {
    const sessionUrl = getSessionUrl();

    return nextStep === 'SETUP' || nextStep === 'DONE'
      ? sessionUrl
      : `${sessionUrl}/${exerciseStepUrlSegments[nextStep as UrlStep]}`;
  };

  const resolveStepFromUrl = (
    sessionStatus?: LearningSession['status'],
  ): Step => {
    if (sessionStatus === 'DONE') {
      return 'DONE';
    }

    return learningExerciseStep
      ? urlSegmentExerciseSteps[learningExerciseStep] || 'SETUP'
      : 'SETUP';
  };

  const navigateToStep = (nextStep: Step) => {
    setStep(nextStep);
    navigate(getStepUrl(nextStep));
  };

  const loadSession = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get<LearningSession>(
        `/learning-sessions/${learningSessionId}`,
      );
      setSession(data);
      setMemoryWords(data.words);
      setLeftWords(data.words);
      setRightWords(shuffle(data.words));
      setSelectedLeftId(null);
      setSelectedRightId(null);
      setMissingWordsExercises([]);
      setMissingWordsIndex(0);
      setMissingWordsAnswers({});
      setMissingWordsManualAnswers({});
      setCheckedMissingWords({});
      setQuestionAnswerExercises([]);
      setQuestionAnswerIndex(0);
      setQuestionAnswerResponses({});
      setShownQuestionAnswerSamples({});
      setStep(resolveStepFromUrl(data.status));
      const existingResults = data.words.reduce<
        Record<number, LearningResultStatus>
      >((acc, word) => {
        if (word.resultStatus) {
          acc[word.id] = word.resultStatus;
        }
        return acc;
      }, {});
      setResults(existingResults);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSession();
    // Refetch only when the session in the URL changes. loadSession also reads
    // learningExerciseStep (via resolveStepFromUrl), so listing it would refetch
    // on every intra-session step change — not what we want.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningSessionId]);

  useEffect(() => {
    if (!session) {
      return;
    }

    const resolvedStep = resolveStepFromUrl(session.status);
    setStep(resolvedStep);

    if (
      learningExerciseStep &&
      session.status !== 'DONE' &&
      !urlSegmentExerciseSteps[learningExerciseStep]
    ) {
      navigate(getSessionUrl(), { replace: true });
    }
    // Reconcile the step/URL only on step or session-status changes; navigate
    // and the URL helpers are stable enough that re-running on their identity
    // would just cause redundant redirects.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningExerciseStep, session?.status]);

  const loadMissingWordsExercises = async () => {
    if (!session || missingWordsLoading) {
      return;
    }

    setMissingWordsLoading(true);
    setError('');
    try {
      const { data } = await axiosInstance.post<MissingWordsExerciseResponse>(
        `/learning-sessions/${session.id}/exercises/missing-words`,
      );
      setMissingWordsExercises(data.exercises || []);
      setMissingWordsIndex(0);
      setMissingWordsAnswers({});
      setMissingWordsManualAnswers({});
      setCheckedMissingWords({});
    } catch {
      setError('Не вдалось підготувати вправу з пропущеними словами.');
    } finally {
      setMissingWordsLoading(false);
    }
  };

  const loadQuestionAnswerExercises = async () => {
    if (!session || questionAnswerLoading) {
      return;
    }

    setQuestionAnswerLoading(true);
    setError('');
    try {
      const { data } =
        await axiosInstance.post<QuestionAnswerExerciseResponse>(
          `/learning-sessions/${session.id}/exercises/question-answer`,
        );
      setQuestionAnswerExercises(data.exercises || []);
      setQuestionAnswerIndex(0);
      setQuestionAnswerResponses({});
      setShownQuestionAnswerSamples({});
    } catch {
      setError('Не вдалось підготувати вправу із запитаннями.');
    } finally {
      setQuestionAnswerLoading(false);
    }
  };

  useEffect(() => {
    if (
      step === 'MISSING_WORDS' &&
      session &&
      !missingWordsExercises.length
    ) {
      loadMissingWordsExercises();
    }
    // Lazily load exercises once when the user enters this step for a session.
    // The in-body guard (!missingWordsExercises.length) prevents re-fetching, so
    // keying on [step, session?.id] is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, session?.id]);

  useEffect(() => {
    if (
      step === 'QUESTION_ANSWER' &&
      session &&
      !questionAnswerExercises.length
    ) {
      loadQuestionAnswerExercises();
    }
    // Lazily load exercises once when the user enters this step for a session.
    // The in-body guard (!questionAnswerExercises.length) prevents re-fetching,
    // so keying on [step, session?.id] is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, session?.id]);

  const animateMatchingColumn = (
    matchingWords: LearningSessionWord[],
    wordRefs: React.MutableRefObject<Record<number, HTMLButtonElement | null>>,
    previousWordPositions: React.MutableRefObject<Map<number, DOMRect>>,
  ) => {
    const previousPositions = previousWordPositions.current;
    if (!previousPositions.size) {
      return;
    }

    const reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    matchingWords.forEach((word) => {
      const element = wordRefs.current[word.id];
      const previousPosition = previousPositions.get(word.id);

      if (!element || !previousPosition) {
        return;
      }

      const currentPosition = element.getBoundingClientRect();
      const translateX = previousPosition.left - currentPosition.left;
      const translateY = previousPosition.top - currentPosition.top;

      if (!translateX && !translateY) {
        return;
      }

      if (reduceMotion) {
        element.style.transform = '';
        element.style.transition = '';
        return;
      }

      element.style.transform = `translate(${translateX}px, ${translateY}px)`;
      element.style.transition = 'none';
      element.style.zIndex = '1';

      window.requestAnimationFrame(() => {
        element.style.transform = '';
        element.style.transition =
          'transform 320ms cubic-bezier(0.22, 1, 0.36, 1)';
      });

      window.setTimeout(() => {
        element.style.transition = '';
        element.style.zIndex = '';
      }, 340);
    });

    previousWordPositions.current = new Map();
  };

  useLayoutEffect(() => {
    animateMatchingColumn(
      leftWords,
      leftWordRefs,
      previousLeftWordPositions,
    );
    animateMatchingColumn(
      rightWords,
      rightWordRefs,
      previousRightWordPositions,
    );
  }, [leftWords, rightWords]);

  const getNextExerciseStep = (currentStep: Step): Step => {
    if (currentStep === 'SETUP') {
      if (useMemory) {
        return 'MEMORY';
      }
      if (useMatching) {
        return 'MATCHING';
      }
      if (useMissingWords) {
        return 'MISSING_WORDS';
      }
      return useQuestionAnswer ? 'QUESTION_ANSWER' : 'SETUP';
    }
    if (currentStep === 'MEMORY') {
      if (useMatching) {
        return 'MATCHING';
      }
      if (useMissingWords) {
        return 'MISSING_WORDS';
      }
      return useQuestionAnswer ? 'QUESTION_ANSWER' : 'SETUP';
    }
    if (currentStep === 'MATCHING') {
      if (useMissingWords) {
        return 'MISSING_WORDS';
      }
      return useQuestionAnswer ? 'QUESTION_ANSWER' : 'SETUP';
    }
    if (currentStep === 'MISSING_WORDS') {
      return useQuestionAnswer ? 'QUESTION_ANSWER' : 'SETUP';
    }
    return 'SETUP';
  };

  const goToNextExercise = () =>
    navigateToStep(getNextExerciseStep(step));

  const isExerciseSelected = (checked: ExerciseOptionKey) => {
    if (checked === 'memory') {
      return useMemory;
    }
    if (checked === 'matching') {
      return useMatching;
    }
    if (checked === 'missingWords') {
      return useMissingWords;
    }
    return useQuestionAnswer;
  };

  const toggleExerciseSelected = (checked: ExerciseOptionKey) => {
    if (checked === 'memory') {
      setUseMemory((value) => !value);
      return;
    }
    if (checked === 'matching') {
      setUseMatching((value) => !value);
      return;
    }
    if (checked === 'missingWords') {
      setUseMissingWords((value) => !value);
      return;
    }
    setUseQuestionAnswer((value) => !value);
  };

  const openMemorySettings = () => {
    setDraftMemoryColumnVisibility(memoryColumnVisibility);
    setDraftBlurredMemoryColumns(blurredMemoryColumns);
    setDraftMemoryColumnOrder(memoryColumnOrder);
    setMemorySettingsOpen(true);
  };

  const moveDraftMemoryColumn = (
    columnKey: MemoryColumnKey,
    direction: -1 | 1,
  ) => {
    setDraftMemoryColumnOrder((columns) => {
      const columnIndex = columns.indexOf(columnKey);
      const nextIndex = columnIndex + direction;

      if (columnIndex < 0 || nextIndex < 0 || nextIndex >= columns.length) {
        return columns;
      }

      const nextColumns = [...columns];
      const [column] = nextColumns.splice(columnIndex, 1);
      nextColumns.splice(nextIndex, 0, column);

      return nextColumns;
    });
  };

  const resetDraftMemorySettings = () => {
    setDraftMemoryColumnVisibility(DEFAULT_MEMORY_COLUMN_VISIBILITY);
    setDraftBlurredMemoryColumns(DEFAULT_MEMORY_COLUMN_BLUR);
    setDraftMemoryColumnOrder(DEFAULT_MEMORY_COLUMN_ORDER);
  };

  const saveMemorySettings = () => {
    setMemoryColumnVisibility(draftMemoryColumnVisibility);
    setBlurredMemoryColumns(draftBlurredMemoryColumns);
    setMemoryColumnOrder(draftMemoryColumnOrder);
    setMemorySettingsOpen(false);
  };

  const getMemoryCellSx = (columnKey: MemoryColumnKey) => ({
    fontSize: LEARNING_EXERCISE_TEXT_SX,
    lineHeight: 1.35,
    px: { xs: 2, md: 4 },
    py: { xs: 1.75, md: 2.25 },
    textAlign: 'center',
    '& .memory-cell-content': {
      display: 'inline-block',
      filter: blurredMemoryColumns[columnKey] ? 'blur(5px)' : 'none',
      transition: 'filter 160ms ease',
      userSelect: blurredMemoryColumns[columnKey] ? 'none' : 'auto',
    },
    '&:hover .memory-cell-content, &:focus-within .memory-cell-content': {
      filter: 'none',
    },
  });

  const renderMemorySettingsDialog = () => (
    <Dialog
      disableScrollLock
      fullWidth
      maxWidth={'sm'}
      open={memorySettingsOpen}
      onClose={() => setMemorySettingsOpen(false)}
    >
      <DialogTitle>{'Налаштування таблиці для запам’ятовування'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack spacing={1}>
            <Typography fontWeight={700}>{'Колонки'}</Typography>
            <Typography color={'text.secondary'} variant={'body2'}>
              {
                'Увімкніть потрібні колонки, додайте blur та змініть порядок стрілками.'
              }
            </Typography>
          </Stack>

          <Stack spacing={1}>
            {draftMemoryColumnOrder.map((columnKey, index) => (
              <Box
                key={columnKey}
                sx={{
                  alignItems: 'center',
                  border: '1px solid',
                  borderColor: 'primary.tableBorder',
                  borderRadius: 1,
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  minHeight: 44,
                  px: 1.5,
                  py: 0.5,
                }}
              >
                <Checkbox
                  checked={draftMemoryColumnVisibility[columnKey]}
                  disableTouchRipple
                  onChange={() =>
                    setDraftMemoryColumnVisibility((columns) => ({
                      ...columns,
                      [columnKey]: !columns[columnKey],
                    }))
                  }
                />
                <Typography sx={{ flex: 1, minWidth: 120 }}>
                  {MEMORY_COLUMN_LABELS[columnKey]}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={draftBlurredMemoryColumns[columnKey]}
                      disabled={!draftMemoryColumnVisibility[columnKey]}
                      onChange={() =>
                        setDraftBlurredMemoryColumns((columns) => ({
                          ...columns,
                          [columnKey]: !columns[columnKey],
                        }))
                      }
                    />
                  }
                  label={'Blur'}
                  sx={{ mr: 0 }}
                />
                <IconButton
                  disabled={index === 0}
                  size={'small'}
                  onClick={() => moveDraftMemoryColumn(columnKey, -1)}
                >
                  <KeyboardArrowUpIcon />
                </IconButton>
                <IconButton
                  disabled={index === draftMemoryColumnOrder.length - 1}
                  size={'small'}
                  onClick={() => moveDraftMemoryColumn(columnKey, 1)}
                >
                  <KeyboardArrowDownIcon />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color={'error'} onClick={resetDraftMemorySettings}>
          {'Скинути'}
        </Button>
        <Button onClick={() => setMemorySettingsOpen(false)}>
          {'Скасувати'}
        </Button>
        <Button onClick={saveMemorySettings} variant={'contained'}>
          {'Зберегти'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  const rememberMatchingColumnPositions = (
    wordRefs: React.MutableRefObject<Record<number, HTMLButtonElement | null>>,
  ) =>
    new Map(
      Object.entries(wordRefs.current)
        .filter(([, element]) => !!element)
        .map(([id, element]) => [
          Number(id),
          element!.getBoundingClientRect(),
        ]),
    );

  const alignRightWordWithLeftWord = (wordId: number) => {
    const leftIndex = leftWords.findIndex((word) => word.id === wordId);
    const rightIndex = rightWords.findIndex((word) => word.id === wordId);

    if (leftIndex >= 0 && rightIndex >= 0 && leftIndex !== rightIndex) {
      previousRightWordPositions.current =
        rememberMatchingColumnPositions(rightWordRefs);

      setRightWords((items) => {
        const nextItems = [...items];
        const itemToMove = nextItems[rightIndex];

        nextItems[rightIndex] = nextItems[leftIndex];
        nextItems[leftIndex] = itemToMove;

        return nextItems;
      });
    }
  };

  const alignLeftWordWithRightWord = (wordId: number) => {
    const leftIndex = leftWords.findIndex((word) => word.id === wordId);
    const rightIndex = rightWords.findIndex((word) => word.id === wordId);

    if (leftIndex >= 0 && rightIndex >= 0 && leftIndex !== rightIndex) {
      previousLeftWordPositions.current =
        rememberMatchingColumnPositions(leftWordRefs);

      setLeftWords((items) => {
        const nextItems = [...items];
        const itemToMove = nextItems[leftIndex];

        nextItems[leftIndex] = nextItems[rightIndex];
        nextItems[rightIndex] = itemToMove;

        return nextItems;
      });
    }
  };

  const clearMatchingSelection = () => {
    setSelectedLeftId(null);
    setSelectedRightId(null);
  };

  const completeMatchingPair = (
    leftWord: LearningSessionWord,
    rightWord: LearningSessionWord,
    selectedSide: MatchingSide,
  ) => {
    if (leftWord.id === rightWord.id) {
      if (selectedSide === 'right') {
        alignRightWordWithLeftWord(leftWord.id);
      } else {
        alignLeftWordWithRightWord(leftWord.id);
      }
      setMatchedIds((ids) => Array.from(new Set([...ids, leftWord.id])));
      clearMatchingSelection();
      return;
    }

    if (selectedSide === 'left') {
      setWrongLeftId(leftWord.id);
      window.setTimeout(() => setWrongLeftId(null), 500);
    } else {
      setWrongRightId(rightWord.id);
      window.setTimeout(() => setWrongRightId(null), 500);
    }
    clearMatchingSelection();
  };

  const selectLeftWord = (leftWord: LearningSessionWord) => {
    if (matchedWordIds.includes(leftWord.id)) {
      return;
    }

    if (selectedRightId !== null) {
      const rightWord = rightWords.find((word) => word.id === selectedRightId);
      if (rightWord) {
        completeMatchingPair(leftWord, rightWord, 'left');
      }
      return;
    }

    setSelectedLeftId((id) => (id === leftWord.id ? null : leftWord.id));
  };

  const selectRightWord = (rightWord: LearningSessionWord) => {
    if (matchedWordIds.includes(rightWord.id)) {
      return;
    }

    if (selectedLeftId !== null) {
      const leftWord = leftWords.find((word) => word.id === selectedLeftId);
      if (leftWord) {
        completeMatchingPair(leftWord, rightWord, 'right');
      }
      return;
    }

    setSelectedRightId((id) => (id === rightWord.id ? null : rightWord.id));
  };

  const getMatchingButtonSx = ({
    matched,
    selected,
    wrong,
  }: {
    matched?: boolean;
    selected?: boolean;
    wrong?: boolean;
  }) => ({
    backgroundColor: wrong
      ? 'error.main'
      : matched
      ? 'primary.vocabularyActiveSpeech'
      : selected
      ? 'primary.main'
      : 'primary.vocabularyUnlearned',
    borderColor: wrong
      ? 'error.main'
      : matched
      ? 'primary.vocabularyActiveSpeech'
      : selected
      ? 'primary.main'
      : 'primary.tableBorder',
    color: matched || selected || wrong ? '#fff' : 'text.primary',
    cursor: matched ? 'default' : 'pointer',
    fontSize: LEARNING_EXERCISE_TEXT_SX,
    fontWeight: 700,
    justifyContent: 'flex-start',
    position: 'relative',
    transition:
      'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
    '&:hover': {
      backgroundColor: wrong
        ? 'error.main'
        : matched
        ? 'primary.vocabularyActiveSpeech'
        : selected
        ? 'primary.main'
        : 'primary.vocabularyUnlearned',
      borderColor: matched || selected || wrong
        ? undefined
        : 'primary.tableBorder',
      color: matched || selected || wrong ? '#fff' : 'text.primary',
    },
  });

  const answerQuiz = (status: LearningResultStatus) => {
    const word = words[quizIndex];
    if (!word) {
      return;
    }

    setResults((state) => ({ ...state, [word.id]: status }));
    setQuizIndex((index) => Math.min(index + 1, words.length - 1));
  };

  const saveResults = async () => {
    if (!session) {
      return;
    }

    setSaving(true);
    setError('');
    try {
      await axiosInstance.post(`/learning-sessions/${session.id}/complete`, {
        results: Object.entries(results).map(
          ([sessionWordId, resultStatus]) => ({
            sessionWordId: Number(sessionWordId),
            resultStatus,
          }),
        ),
      });
      setStep('DONE');
      navigate(pagePaths.learning);
    } catch {
      setError('Не вдалось зберегти результати вправ.');
    } finally {
      setSaving(false);
    }
  };

  const renderSetup = () => (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Typography variant="h5" fontWeight={700} mb={1}>
            {'Слова в сесії'}
          </Typography>
          <Grid container spacing={1}>
            {words.map((word) => (
              <Grid key={word.id}>
                <Chip
                  label={getWordTitle(word)}
                  variant="outlined"
                  color={word.isLearnedBefore ? 'success' : 'default'}
                />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Stack spacing={2}>
            <Typography variant="h5" fontWeight={700}>
              {'Обрані вправи'}
            </Typography>
            <Grid container spacing={1.5}>
              {exerciseOptions.map(({ checked, description, title }) => {
                const selected = isExerciseSelected(checked);
                const toggleSelected = () => toggleExerciseSelected(checked);

                return (
                  <Grid key={checked} size={{ xs: 12, md: 6 }}>
                    <Box
                      onClick={toggleSelected}
                      sx={{
                        border: '1px solid',
                        borderColor: selected
                          ? 'primary.main'
                          : 'primary.tableBorder',
                        borderRadius: 1,
                        cursor: 'pointer',
                        height: '100%',
                        p: 1.5,
                      }}
                    >
                      <Stack
                        alignItems="flex-start"
                        direction="row"
                        spacing={1}
                      >
                        <Checkbox
                          checked={selected}
                          onChange={toggleSelected}
                          onClick={(event) => event.stopPropagation()}
                          sx={{ p: 0.25 }}
                        />
                        <Box>
                          <Typography fontWeight={700}>{title}</Typography>
                          <Typography color="text.secondary" variant="body2">
                            {description}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            <Button
              disabled={!hasSelectedExercises}
              fullWidth
              onClick={goToNextExercise}
            >
              {'Почати вправи'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Stack spacing={2}>
            <Typography variant="h5" fontWeight={700}>
              {'Збереження слів у Vocabulary'}
            </Typography>
            <Grid container spacing={1.5}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.tableBorder',
                    borderRadius: 1,
                    height: '100%',
                    p: 1.5,
                  }}
                >
                  <Typography fontWeight={700}>
                    {'Спрощене збереження слів'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {'Промаркувати слова та зберегти / оновити їх у Vocabulary'}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box
                  onClick={() => navigateToStep('QUIZ')}
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                    cursor: 'pointer',
                    height: '100%',
                    p: 1.5,
                  }}
                >
                  <Typography fontWeight={700}>
                    {'Тестування та збереження'}
                  </Typography>
                  <Typography color="text.secondary" variant="body2">
                    {
                      'Пройти тестування на засвоєння та зберегти чи оновити слова у Vocabulary'
                    }
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  );

  const renderMemory = () => {
    const memoryColumns: Record<
      MemoryColumnKey,
      {
        label: string;
        visible: boolean;
        render: (word: LearningSessionWord) => React.ReactNode;
      }
    > = {
      word: {
        label: MEMORY_COLUMN_LABELS.word,
        visible: memoryColumnVisibility.word,
        render: (word) => (
          <>
            <Typography fontSize="inherit" fontWeight={700}>
              {getWordTitle(word)}
            </Typography>
            <Typography color="text.secondary" fontSize={{ xs: 14, md: 16 }}>
              {getWordForms(word)}
            </Typography>
          </>
        ),
      },
      definition: {
        label: MEMORY_COLUMN_LABELS.definition,
        visible: memoryColumnVisibility.definition,
        render: (word) => getWordHint(word),
      },
      translation: {
        label: MEMORY_COLUMN_LABELS.translation,
        visible: memoryColumnVisibility.translation,
        render: (word) => word.translation,
      },
    };
    const visibleMemoryColumns = memoryColumnOrder.filter(
      (columnKey) => memoryColumns[columnKey].visible,
    );
    const memoryTableWidth = visibleMemoryColumns.length <= 2 ? 760 : 980;

    return (
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<ShuffleIcon />}
            onClick={() => setMemoryWords((items) => shuffle(items))}
          >
            {'Перемішати'}
          </Button>
          <Button onClick={openMemorySettings}>
            <SettingsIcon />
          </Button>
          <Button onClick={goToNextExercise}>{'Далі'}</Button>
        </Stack>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                overflowX: 'auto',
              }}
            >
              <Box
                sx={{
                  maxWidth: { xs: 'none', md: '100%' },
                  minWidth: { xs: 520, md: 0 },
                  width: { xs: 520, md: memoryTableWidth },
                }}
              >
              <Table size="small" sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    {visibleMemoryColumns.map((columnKey) => (
                      <TableCell
                        key={columnKey}
                        sx={{
                          px: { xs: 2, md: 4 },
                          py: 1.5,
                          textAlign: 'center',
                        }}
                      >
                        <Typography fontSize={18} fontWeight={700}>
                          {memoryColumns[columnKey].label}
                        </Typography>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {memoryWords.map((word) => (
                    <TableRow key={word.id}>
                      {visibleMemoryColumns.map((columnKey) => (
                        <TableCell
                          key={columnKey}
                          sx={getMemoryCellSx(columnKey)}
                          tabIndex={blurredMemoryColumns[columnKey] ? 0 : -1}
                        >
                          <Box className="memory-cell-content">
                            {memoryColumns[columnKey].render(word)}
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    );
  };

  const renderMatching = () => (
    <Stack spacing={2}>
      <Stack direction="row" justifyContent="flex-end" spacing={1}>
        <Button
          variant="outlined"
          startIcon={<ShuffleIcon />}
          onClick={() => {
            setMatchedIds([]);
            setSelectedLeftId(null);
            setSelectedRightId(null);
            setWrongLeftId(null);
            setWrongRightId(null);
            setLeftWords(words);
            setRightWords(shuffle(words));
          }}
        >
          {'Спочатку'}
        </Button>
        <Button disabled={!matchedDone} onClick={goToNextExercise}>
          {'Далі'}
        </Button>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                {leftWords.map((word) => {
                  const matched = matchedWordIds.includes(word.id);
                  const selected = selectedLeftId === word.id;
                  const wrong = wrongLeftId === word.id;
                  return (
                    <Button
                      key={word.id}
                      ref={(element) => {
                        leftWordRefs.current[word.id] = element;
                      }}
                      variant={'outlined'}
                      onClick={() =>
                        matched ? undefined : selectLeftWord(word)
                      }
                      sx={getMatchingButtonSx({ matched, selected, wrong })}
                    >
                      {matched ? <CheckIcon fontSize="small" /> : null}
                      <Box component="span" ml={matched ? 1 : 0}>
                        {getWordTitle(word)}
                      </Box>
                    </Button>
                  );
                })}
              </Stack>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack spacing={1}>
                {rightWords.map((word) => {
                  const matched = matchedWordIds.includes(word.id);
                  const selected = selectedRightId === word.id;
                  const wrong = wrongRightId === word.id;

                  return (
                    <Button
                      key={word.id}
                      ref={(element) => {
                        rightWordRefs.current[word.id] = element;
                      }}
                      variant={'outlined'}
                      onClick={() =>
                        matched ? undefined : selectRightWord(word)
                      }
                      sx={getMatchingButtonSx({ matched, selected, wrong })}
                    >
                      {word.translation || 'Переклад відсутній'}
                    </Button>
                  );
                })}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  );

  const normalizeExerciseAnswer = (value = '') =>
    value.trim().toLowerCase().replace(/\s+/g, ' ');

  const isMissingWordsAnswerCorrect = (
    exercise: MissingWordsExercise,
    answer?: string,
  ) => {
    const normalizedAnswer = normalizeExerciseAnswer(answer);

    if (!normalizedAnswer) {
      return false;
    }

    const acceptableAnswers =
      exercise.acceptableAnswers?.length
        ? exercise.acceptableAnswers
        : [exercise.answer];

    return acceptableAnswers.some(
      (acceptableAnswer) =>
        normalizeExerciseAnswer(acceptableAnswer) === normalizedAnswer,
    );
  };

  const renderMissingWords = () => {
    const exercise = missingWordsExercises[missingWordsIndex];
    const manualAnswer = exercise
      ? missingWordsManualAnswers[exercise.id] || ''
      : '';
    const selectedAnswer = exercise
      ? missingWordsAnswers[exercise.id] || manualAnswer || undefined
      : undefined;
    const isAnswerChecked = exercise
      ? !!checkedMissingWords[exercise.id]
      : false;
    const selectedAnswerCorrect = exercise
      ? isMissingWordsAnswerCorrect(exercise, selectedAnswer)
      : false;
    const sentenceParts = exercise
      ? exercise.sentence.split('___')
      : ['', ''];
    const isLastExercise =
      missingWordsIndex >= missingWordsExercises.length - 1;

    const setMissingWordsAnswer = (answer: string) => {
      if (!exercise || isAnswerChecked) {
        return;
      }

      setMissingWordsAnswers((answers) => ({
        ...answers,
        [exercise.id]: answer,
      }));
      setMissingWordsManualAnswers((answers) => ({
        ...answers,
        [exercise.id]: answer,
      }));
      setCheckedMissingWords((checked) => ({
        ...checked,
        [exercise.id]: isMissingWordsAnswerCorrect(exercise, answer),
      }));
    };

    const updateManualAnswer = (answer: string) => {
      if (!exercise || isAnswerChecked) {
        return;
      }

      setMissingWordsManualAnswers((answers) => ({
        ...answers,
        [exercise.id]: answer,
      }));
      setMissingWordsAnswers((answers) => ({
        ...answers,
        [exercise.id]: answer,
      }));
      setCheckedMissingWords((checked) => ({
        ...checked,
        [exercise.id]: isMissingWordsAnswerCorrect(exercise, answer),
      }));
    };

    const goToPreviousMissingWordsExercise = () => {
      if (missingWordsIndex > 0) {
        setMissingWordsIndex((index) => index - 1);
      }
    };

    const goToNextMissingWordsExercise = () => {
      if (!isLastExercise) {
        setMissingWordsIndex((index) => index + 1);
        return;
      }
      goToNextExercise();
    };

    if (missingWordsLoading) {
      return (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <WithLoader loading={missingWordsLoading}>{null}</WithLoader>
          </CardContent>
        </Card>
      );
    }

    if (!exercise) {
      return (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={goToNextExercise}>{'Далі'}</Button>
          </Stack>
          <UIEmptyState
            icon={<RefreshIcon />}
            title={'Немає підготовлених вправ'}
            description={
              'Генерація вправ із пропущеними словами не вдалась. Спробуй ще раз або пропусти крок.'
            }
            action={{
              label: 'Згенерувати ще раз',
              icon: <RefreshIcon />,
              onClick: () => loadMissingWordsExercises(),
              disabled: missingWordsLoading,
            }}
          />
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        <Box sx={{ position: 'relative' }}>
          <IconButton
            disabled={missingWordsIndex === 0}
            onClick={goToPreviousMissingWordsExercise}
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'primary.tableBorder',
              boxShadow: 1,
              height: { xs: 44, md: 56 },
              left: { xs: -6, md: -26 },
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              width: { xs: 44, md: 56 },
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'background.paper',
                boxShadow: 3,
              },
            }}
          >
            <KeyboardArrowLeftIcon sx={{ fontSize: { xs: 30, md: 40 } }} />
          </IconButton>
          <IconButton
            disabled={!isAnswerChecked}
            onClick={goToNextMissingWordsExercise}
            sx={{
              backgroundColor: 'background.paper',
              border: '1px solid',
              borderColor: 'primary.tableBorder',
              boxShadow: 1,
              height: { xs: 44, md: 56 },
              position: 'absolute',
              right: { xs: -6, md: -26 },
              top: '50%',
              transform: 'translateY(-50%)',
              width: { xs: 44, md: 56 },
              zIndex: 2,
              '&:hover': {
                backgroundColor: 'background.paper',
                boxShadow: 3,
              },
            }}
          >
            <KeyboardArrowRightIcon sx={{ fontSize: { xs: 30, md: 40 } }} />
          </IconButton>

        <Card variant="outlined" sx={{ borderRadius: 2, mx: { xs: 2, md: 3 } }}>
          <CardContent>
            <Stack spacing={3}>
              <Typography color="text.secondary" variant="overline">
                {`${missingWordsIndex + 1} / ${missingWordsExercises.length}`}
              </Typography>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 1, md: 1.5 },
                  justifyContent: 'center',
                  textAlign: 'center',
                }}
              >
                <Typography
                  component="span"
                  fontWeight={700}
                  sx={{ fontSize: { xs: 24, md: 32 } }}
                >
                  {sentenceParts[0]}
                </Typography>
                <Box
                  sx={{
                    alignItems: 'center',
                    display: 'inline-flex',
                    gap: 0.75,
                  }}
                >
                  <TextField
                    disabled={isAnswerChecked}
                    size="small"
                    value={manualAnswer}
                    onChange={(event) => updateManualAnswer(event.target.value)}
                    sx={{
                      minWidth: { xs: 150, md: 220 },
                      width: { xs: 'min(68vw, 260px)', md: 260 },
                      '& .MuiInputBase-root': {
                        backgroundColor: selectedAnswer
                          ? selectedAnswerCorrect
                            ? 'primary.vocabularyActiveSpeech'
                            : 'error.light'
                          : 'primary.vocabularyUnlearned',
                        borderRadius: 1,
                        fontSize: { xs: 22, md: 28 },
                        fontWeight: 700,
                      },
                      '& .MuiInputBase-input': {
                        py: 1,
                        textAlign: 'center',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: selectedAnswer
                          ? selectedAnswerCorrect
                            ? 'primary.vocabularyActiveSpeech'
                            : 'error.main'
                          : 'primary.main',
                        borderWidth: 2,
                      },
                    }}
                  />
                  {exercise.hint ? (
                    <Tooltip arrow placement="top" title={exercise.hint}>
                      <IconButton
                        size="small"
                        sx={{
                          border: '1px solid',
                          borderColor: 'primary.tableBorder',
                          color: 'primary.main',
                          flexShrink: 0,
                          height: 34,
                          width: 34,
                          '&:hover': {
                            backgroundColor: 'primary.vocabularyUnlearned',
                          },
                        }}
                      >
                        <HelpOutlineIcon sx={{ fontSize: 19 }} />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </Box>
                <Typography
                  component="span"
                  fontWeight={700}
                  sx={{ fontSize: { xs: 24, md: 32 } }}
                >
                  {sentenceParts.slice(1).join('___')}
                </Typography>
              </Box>

              <Grid container spacing={1.5}>
                {exercise.options.map((option) => {
                  const isSelected = selectedAnswer === option;
                  const isCorrect = isMissingWordsAnswerCorrect(
                    exercise,
                    option,
                  );
                  const showResult = isAnswerChecked;
                  const isWrongSelection = isSelected && !isCorrect;

                  return (
                    <Grid key={option} size={{ xs: 12, md: 6 }}>
                      <Button
                        fullWidth
                        disabled={showResult}
                        variant="outlined"
                        onClick={() => setMissingWordsAnswer(option)}
                        sx={{
                          backgroundColor:
                            showResult && isCorrect
                              ? 'primary.vocabularyActiveSpeech'
                              : isWrongSelection
                              ? 'error.main'
                              : 'primary.vocabularyUnlearned',
                          borderColor:
                            showResult && (isCorrect || isWrongSelection)
                              ? 'transparent'
                              : 'primary.tableBorder',
                          color:
                            showResult && isWrongSelection
                              ? '#fff'
                              : 'text.primary',
                          justifyContent: 'center',
                          minHeight: 48,
                          '&.Mui-disabled': {
                            backgroundColor:
                              showResult && isCorrect
                                ? 'primary.vocabularyActiveSpeech'
                                : isWrongSelection
                                ? 'error.main'
                                : 'primary.vocabularyUnlearned',
                            borderColor:
                              showResult && (isCorrect || isWrongSelection)
                                ? 'transparent'
                                : 'primary.tableBorder',
                            color:
                              showResult && isWrongSelection
                                ? '#fff'
                                : 'text.primary',
                          },
                          '&:hover': {
                            backgroundColor: showResult
                              ? undefined
                              : 'primary.vocabularyUnlearned',
                            color: 'text.primary',
                          },
                        }}
                      >
                        {option}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>

            </Stack>
          </CardContent>
        </Card>
        </Box>
      </Stack>
    );
  };

  const renderQuestionAnswer = () => {
    const exercise = questionAnswerExercises[questionAnswerIndex];
    const response = exercise
      ? questionAnswerResponses[exercise.id] || ''
      : '';
    const sampleShown = exercise
      ? !!shownQuestionAnswerSamples[exercise.id]
      : false;
    const isLastExercise =
      questionAnswerIndex >= questionAnswerExercises.length - 1;

    const goToNextQuestionAnswerExercise = () => {
      if (!isLastExercise) {
        setQuestionAnswerIndex((index) => index + 1);
        return;
      }
      goToNextExercise();
    };

    if (questionAnswerLoading) {
      return (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <WithLoader loading={questionAnswerLoading}>{null}</WithLoader>
          </CardContent>
        </Card>
      );
    }

    if (!exercise) {
      return (
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            <Button onClick={goToNextExercise}>{'Далі'}</Button>
          </Stack>
          <UIEmptyState
            icon={<RefreshIcon />}
            title={'Немає підготовлених вправ'}
            description={
              'Генерація вправ із запитаннями не вдалась. Спробуй ще раз або пропусти крок.'
            }
            action={{
              label: 'Згенерувати ще раз',
              icon: <RefreshIcon />,
              onClick: () => loadQuestionAnswerExercises(),
              disabled: questionAnswerLoading,
            }}
          />
        </Stack>
      );
    }

    return (
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button
            disabled={!response.trim()}
            onClick={goToNextQuestionAnswerExercise}
          >
            {isLastExercise ? 'Завершити вправу' : 'Далі'}
          </Button>
        </Stack>

        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <CardContent>
            <Stack spacing={3}>
              <Typography color="text.secondary" variant="overline">
                {`${questionAnswerIndex + 1} / ${
                  questionAnswerExercises.length
                }`}
              </Typography>
              <Box>
                <Chip
                  label={exercise.targetWord || exercise.word}
                  color="primary"
                  sx={{ mb: 2 }}
                />
                <Typography
                  fontWeight={700}
                  sx={{ fontSize: { xs: 24, md: 32 } }}
                >
                  {exercise.question}
                </Typography>
              </Box>

              {exercise.hint ? (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.tableBorder',
                    borderRadius: 1,
                    p: 2,
                  }}
                >
                  <Typography color="text.secondary" variant="body2">
                    {'Підказка'}
                  </Typography>
                  <Typography>{exercise.hint}</Typography>
                </Box>
              ) : null}

              <TextField
                fullWidth
                multiline
                minRows={4}
                placeholder={'Напиши відповідь англійською'}
                value={response}
                onChange={(event) =>
                  setQuestionAnswerResponses((answers) => ({
                    ...answers,
                    [exercise.id]: event.target.value,
                  }))
                }
              />

              <Stack alignItems="flex-start" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={() =>
                    setShownQuestionAnswerSamples((samples) => ({
                      ...samples,
                      [exercise.id]: !samples[exercise.id],
                    }))
                  }
                >
                  {sampleShown ? 'Сховати приклад' : 'Показати приклад'}
                </Button>
                {sampleShown ? (
                  <Box
                    sx={{
                      backgroundColor: 'primary.vocabularyUnlearned',
                      border: '1px solid',
                      borderColor: 'primary.tableBorder',
                      borderRadius: 1,
                      p: 2,
                      width: '100%',
                    }}
                  >
                    <Typography>{exercise.sampleAnswer}</Typography>
                  </Box>
                ) : null}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    );
  };

  const renderQuiz = () => {
    const word = words[quizIndex];

    return (
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {'Збереження результатів'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {'Оціни кожне слово, щоб оновити його статус у Vocabulary.'}
              </Typography>
            </Box>

            {word && !quizDone && (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 3,
                }}
              >
                <Typography variant="overline" color="text.secondary">
                  {`${quizIndex + 1} / ${words.length}`}
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {getWordTitle(word)}
                </Typography>
                <Typography mt={1}>{word.translation}</Typography>
                <Typography mt={1} color="text.secondary">
                  {getWordHint(word)}
                </Typography>
              </Box>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
              <Button onClick={() => answerQuiz('LEARNED')}>
                {resultLabels.LEARNED}
              </Button>
              <Button variant="outlined" onClick={() => answerQuiz('REPEAT')}>
                {resultLabels.REPEAT}
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                onClick={() => answerQuiz('NOT_LEARNED')}
              >
                {resultLabels.NOT_LEARNED}
              </Button>
            </Stack>

            <Divider />

            <Stack spacing={1}>
              {words.map((item) => (
                <Stack
                  key={item.id}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                >
                  <Typography>{getWordTitle(item)}</Typography>
                  <Chip
                    size="small"
                    label={
                      results[item.id]
                        ? resultLabels[results[item.id]]
                        : 'Без оцінки'
                    }
                    color={results[item.id] ? 'primary' : 'default'}
                  />
                </Stack>
              ))}
            </Stack>

            <Stack direction="row" justifyContent="flex-end">
              <Button disabled={!quizDone || saving} onClick={saveResults}>
                {saving ? 'Збереження...' : 'Зберегти результати'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  const renderContent = () => {
    if (!session) {
      return <Alert severity="warning">{'Сесію вправ не знайдено.'}</Alert>;
    }
    if (step === 'SETUP') {
      return renderSetup();
    }
    if (step === 'MEMORY') {
      return renderMemory();
    }
    if (step === 'MATCHING') {
      return renderMatching();
    }
    if (step === 'MISSING_WORDS') {
      return renderMissingWords();
    }
    if (step === 'QUESTION_ANSWER') {
      return renderQuestionAnswer();
    }
    if (step === 'QUIZ') {
      return renderQuiz();
    }

    return (
      <Alert severity="success">
        {'Сесію завершено. Результати вже збережені у Vocabulary.'}
      </Alert>
    );
  };

  return (
    <>
      <UIContainer>
        <BreadCrumbs
          currentLabel={formatLearningSessionTitle(session?.createdAt)}
          currentOnClick={() => navigateToStep('SETUP')}
          extraItems={
            exerciseBreadcrumbLabel
              ? [
                  {
                    label: exerciseBreadcrumbLabel,
                  },
                ]
              : []
          }
        />
      </UIContainer>
      <UIContainer>
        <Box pb={4} pt={1}>
          <WithLoader loading={loading}>
            <Stack spacing={3}>
              {error && <Alert severity="error">{error}</Alert>}
              {renderContent()}
            </Stack>
          </WithLoader>
        </Box>
      </UIContainer>
      {renderMemorySettingsDialog()}
    </>
  );
};

export default LearningSessionPage;
